import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import type { SxProps } from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem'
import * as React from 'react'
import { createContext, FC, ReactNode, useContext, useEffect, useId, useState } from 'react'

import ThemeNew from '../theme/themeNew'

import ButtonNew, { ButtonSize, ButtonStyle, ButtonVariant } from './ButtonNew'

/* Shared context for main and submenu to communicate */
type DropdownCtx = {
    closeAll: () => void
    closeOnClick: boolean
    closeMainMenuOnSubmenuClose: boolean
    activeSubmenuKey: string | null
    setActiveSubmenuKey: (k: string | null) => void
}
const DropdownContext = createContext<DropdownCtx | null>(null)
const useDropdownCtx = () => {
    const ctx = useContext(DropdownContext)
    if (!ctx) {
        throw new Error('Dropdown subcomponents must be used inside <DropdownNew />')
    }
    return ctx
}

export const DropdownItemNew: FC<
    MenuItemProps & {
        leftIcon?: React.ReactNode
    }
> = ({ sx, leftIcon, onClick, children, ...props }) => {
    const { palette } = ThemeNew
    const { closeAll, closeOnClick, setActiveSubmenuKey } = useDropdownCtx()

    const handleClick = (e: React.MouseEvent<HTMLLIElement>) => {
        // Clicking a plain item should close any open submenu and the whole dropdown (if configured)
        setActiveSubmenuKey(null)
        onClick?.(e)
        if (closeOnClick) closeAll()
    }

    return (
        <MenuItem
            {...props}
            onClick={handleClick}
            sx={{
                color: `Grey900`,
                fontSize: '14px',
                backgroundColor: props.selected ? `${palette.Grey100} !important` : `transparent`,
                overflow: 'hidden',
                ...sx,
            }}
        >
            {leftIcon && (
                <ListItemIcon sx={{ minWidth: PAPER_DEFAULT_WIDTH }}>{leftIcon}</ListItemIcon>
            )}
            <ListItemText
                sx={{
                    '> p': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                }}
                primaryTypographyProps={{ variant: 'body2', sx: { color: 'Grey900' } }}
                primary={children}
            />
        </MenuItem>
    )
}

type DropdownSubmenuItemProps = {
    label: ReactNode
    leftIcon?: ReactNode
    children: ReactNode
    iconSx?: SxProps
    menuSx?: SxProps
    itemSx?: SxProps
    submenuKey?: string
    onOpen?: () => void
}

const { palette, spacing } = ThemeNew
const PAPER_DEFAULT_WIDTH = spacing(36)

export const DropdownSubmenuItem: FC<DropdownSubmenuItemProps> = ({
    label,
    leftIcon,
    children,
    iconSx,
    menuSx,
    itemSx,
    submenuKey,
    onOpen,
}) => {
    const localId = useId()
    const key = submenuKey ?? localId
    const { closeAll, activeSubmenuKey, setActiveSubmenuKey, closeMainMenuOnSubmenuClose } =
        useDropdownCtx()

    const isOpen = activeSubmenuKey === key
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const handleParentClick = (e: React.MouseEvent<HTMLElement>) => {
        const el = e.currentTarget
        if (isOpen) {
            setAnchorEl(null)
            setActiveSubmenuKey(null)
        } else {
            setAnchorEl(el)
            setActiveSubmenuKey(key)
            onOpen?.()
        }
    }

    useEffect(() => {
        if (!isOpen) {
            setAnchorEl(null)
        }
    }, [isOpen])

    const handleSubmenuClose = () => {
        setActiveSubmenuKey(null)
        // Close main menu when submenu closes if configured
        if (closeMainMenuOnSubmenuClose) {
            closeAll()
        }
    }

    const childCtxValue = {
        closeAll,
        closeOnClick: true,
        closeMainMenuOnSubmenuClose,
        activeSubmenuKey,
        setActiveSubmenuKey,
    }

    return (
        <>
            <MenuItem
                onClick={handleParentClick}
                aria-haspopup="menu"
                aria-expanded={isOpen ? 'true' : undefined}
                sx={{
                    color: `Grey900`,
                    fontSize: '14px',
                    backgroundColor: isOpen ? `${palette.Grey100} !important` : `transparent`,
                    display: 'flex',
                    alignItems: 'center',
                    ...itemSx,
                }}
            >
                {leftIcon ? (
                    <ListItemIcon sx={{ minWidth: 32, ...iconSx }}>{leftIcon}</ListItemIcon>
                ) : null}
                <ListItemText
                    primaryTypographyProps={{ variant: 'body2', sx: { color: 'Grey900' } }}
                    primary={label}
                />
                <ChevronRightIcon fontSize="small" />
            </MenuItem>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleSubmenuClose}
                disableEnforceFocus
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                style={{ pointerEvents: 'none' }}
                slotProps={{
                    paper: {
                        style: { pointerEvents: 'auto' },
                    },
                }}
                sx={{
                    '.MuiPaper-root': {
                        mt: -1,
                        borderRadius: '8px',
                        minWidth: PAPER_DEFAULT_WIDTH,
                        maxWidth: PAPER_DEFAULT_WIDTH,
                    },
                    ...(menuSx ?? {}),
                }}
            >
                <DropdownContext.Provider value={childCtxValue}>
                    {children}
                </DropdownContext.Provider>
            </Menu>
        </>
    )
}

const DropdownNew: FC<{
    buttonLabel?: ReactNode
    trigger?: ReactNode
    buttonVariant?: ButtonVariant
    buttonStyle?: ButtonStyle
    buttonSize?: ButtonSize
    menuPadding?: string
    closeOnClick?: boolean
    closeMainMenuOnSubmenuClose?: boolean
    menuAutoWidth?: boolean
    compact?: boolean
    sx?: SxProps
    labelSx?: SxProps
    paperSx?: SxProps
    dataTestId?: string
    leftIcon?: ReactNode
    rightIcon?: ReactNode
    onOpen?: () => void
    children: ReactNode
}> = ({
    buttonLabel,
    trigger,
    buttonSize,
    buttonStyle,
    buttonVariant,
    menuPadding,
    children,
    closeOnClick = true,
    closeMainMenuOnSubmenuClose = false,
    compact,
    menuAutoWidth,
    sx,
    labelSx,
    paperSx,
    leftIcon,
    rightIcon,
    dataTestId,
    onOpen,
    ...rest
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const [activeSubmenuKey, setActiveSubmenuKey] = useState<string | null>(null)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!open) {
            onOpen?.()
        }
        setAnchorEl(event.currentTarget)
    }
    const handleCloseAll = () => {
        setActiveSubmenuKey(null)
        setAnchorEl(null)
    }

    const ctxValue = {
        closeAll: handleCloseAll,
        closeOnClick,
        closeMainMenuOnSubmenuClose,
        activeSubmenuKey,
        setActiveSubmenuKey,
    }

    // Clone trigger element and add necessary props
    const renderTrigger = () => {
        if (trigger) {
            const triggerElement = trigger as React.ReactElement
            const existingOnClick = triggerElement.props?.onClick
            return React.cloneElement(triggerElement, {
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                    existingOnClick?.(e)
                    handleClick(e)
                },
                'aria-haspopup': 'true',
                'aria-expanded': open ? 'true' : undefined,
                'data-testid': dataTestId,
            })
        }

        return (
            <ButtonNew
                variant={buttonVariant}
                style={buttonStyle}
                size={compact ? 'sm' : buttonSize}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                leadingIcon={leftIcon}
                trailingIcon={rightIcon}
                onClick={handleClick}
                sx={
                    {
                        '.MuiTypography-root': {
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            ...(labelSx ?? {}),
                        },
                        ...sx,
                    } as SxProps
                }
                {...rest}
                data-testid={dataTestId}
            >
                {buttonLabel}
            </ButtonNew>
        )
    }

    return (
        <DropdownContext.Provider value={ctxValue}>
            {renderTrigger()}
            <Menu
                id={`dropdown-panel-${buttonLabel ?? 'custom-trigger'}`}
                MenuListProps={{
                    'aria-labelledby': `dropdown-btn-${buttonLabel ?? 'custom-trigger'}`,
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseAll}
                elevation={4}
                data-testid={`${dataTestId}-overlay`}
                sx={
                    {
                        '.MuiPaper-root': {
                            mt: 1,
                            borderRadius: '8px',
                            minWidth: menuAutoWidth ? 'auto' : PAPER_DEFAULT_WIDTH,
                            ...(menuPadding ? { padding: menuPadding } : {}),
                            ...(paperSx ?? {}),
                        },
                    } as SxProps<typeof ThemeNew>
                }
            >
                {children}
            </Menu>
        </DropdownContext.Provider>
    )
}

export default DropdownNew
